import {ChangeDetectionStrategy, Component, computed, inject} from "@angular/core"
import {ButtonComponent} from "@app/common/components/buttons/button/button.component"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import type {TemplateGraph} from "@cm/template-nodes"
import type {MattressComponentInstance, MattressInterfaceDescriptor} from "@cm/mattress-nodes"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent} from "@template-editor/components/value-slot/value-slot.component"
import {wrapInterfaceId} from "@cm/template-nodes"
import {schemaForDescriptor, mattressComponentInstanceParametersParameterValue, findClosestParentComponentGraph} from "@cm/mattress-nodes"
import {MattressInspectorService} from "@template-editor/components/inspectors/mattress-inspector/mattress-inspector.service"
import {
    MattressComponentInstanceInterfaceInspectorComponent,
    type MattressInputParameterProvider,
    selectionPossibilitiesForDescriptor,
} from "@template-editor/components/inspectors/mattress-inspector/components/mattress-component-instance-interface-inspector/mattress-component-instance-interface-inspector.component"

@Component({
    selector: "cm-mattress-component-instance-inspector",
    templateUrl: "./mattress-component-instance-inspector.component.html",
    styleUrl: "./mattress-component-instance-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent, MattressComponentInstanceInterfaceInspectorComponent, ButtonComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MattressComponentInstanceInspectorComponent extends BaseInspectorComponent<MattressComponentInstance> {
    mattressInspectorService = inject(MattressInspectorService)

    protected readonly $mattress = computed(() => {
        const instance = this.$node()
        return instance.getParentMattressNode()
    })

    protected readonly $wrappedInstanceId = computed(() => {
        const instance = this.$node()
        const instanceId = instance.parameters.id
        const mattress = this.$mattress()
        const focusedInstanceId = mattress.getFocusedComponentInstanceWrappedId(this.sceneManagerService)
        return focusedInstanceId ? wrapInterfaceId(focusedInstanceId, instanceId) : instanceId
    })

    protected readonly $componentDescriptors = computed(() => {
        const wrappedInstanceId = this.$wrappedInstanceId()
        if (!wrappedInstanceId) return []

        const mattress = this.$mattress()
        return mattress.getComponentDescriptorsForInstance(wrappedInstanceId, this.sceneManagerService)
    })

    protected readonly $templateDescriptors = computed(() => {
        const wrappedInstanceId = this.$wrappedInstanceId()
        if (!wrappedInstanceId) return []

        const mattress = this.$mattress()
        return mattress.getTemplateDescriptorsForInstance(wrappedInstanceId, this.sceneManagerService)
    })

    readonly $inputParameterProvider = computed<MattressInputParameterProvider>(() => {
        const parameters = this.$parameters()
        return ((key: string, descriptor: MattressInterfaceDescriptor) => ({
            selectionPossibilities: selectionPossibilitiesForDescriptor(descriptor),
            overwrittenValue: parameters?.parameters[key] ?? null,
            schema: schemaForDescriptor(descriptor),
        })) as MattressInputParameterProvider
    })

    updateOverwrittenValue(key: string, value: unknown) {
        const result = mattressComponentInstanceParametersParameterValue.safeParse(value)
        if (!result.success) return
        const instance = this.$node()
        this.sceneManagerService.modifyTemplateGraph(() => {
            instance.updateParameters({
                parameters: {...instance.parameters.parameters, [key]: result.data},
            })
        })
        this.onUpdatedParameters()
    }

    addInstanceOutputToParentGraph(outputDescriptor: MattressInterfaceDescriptor) {
        const instance = this.$node()
        const parentGraph = findClosestParentComponentGraph(instance)
        instance.addInstanceOutputToGraph(outputDescriptor, parentGraph, this)
        this.mattressInspectorService.onMattressParametersUpdated()
    }

    clearInstanceParametersParameters() {
        const instance = this.$node()
        this.sceneManagerService.modifyTemplateGraph(() => {
            instance.updateParameters({parameters: {}})
        })
        this.onUpdatedParameters()
    }

    modifyTemplateGraph = (modifier: (x: TemplateGraph) => void) => this.sceneManagerService.modifyTemplateGraph(modifier)
}
